import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRefSize } from "uikit";
import { isBoolean, isUndefined } from "lodash";

import { Language } from "../../../../../services";
import { useTypedDispatch, useTypedSelector } from "../../../../../redux/store";
import orderPage from "../../../../../redux/reducers/OrdersPage";
import { useCurrencyGlobalSettings } from "../../../../../hooks";
import Divider from "../../../../Divider";
import { AccessGuard } from "../../../../common";
import { TabAccessNames, TabKeys } from "../../constants/access";
import { ORDERS_TYPES } from "../../../../../redux/constants/OrdersPage";
import { OrderStatus } from "../../../../../types";

import useBottomData from "./hooks/useBottomData";
import useTopData from "./hooks/useTopData";
import Root from "./components/Root";
import Top from "./components/Top";
import Field from "./components/Field";
import OrderPaymentStatusBadge from "../../../../OrderPaymentStatusBadge";
import Bottom from "./components/Bottom";
import EditButton from "./components/EditButton";
import PriceLabel from "./components/PriceLabel";
import AdditionalPriceLabel from "./components/AdditionalPriceLabel";
import OrderReceipt from "../Receipt";

const Info: React.FC<Info.Props> = ({ language }) => {
	const { t } = useTranslation();
	const dispatch = useTypedDispatch();
	const currencyGlobalSettings = useCurrencyGlobalSettings();
	const activeOrder = useTypedSelector(
		(state) => state.ordersPageReducer.activeOrder,
	);
	const ordersType = useTypedSelector(
		(state) => state.ordersPageReducer.ordersType,
	);
	const { ref: rootRef, size: rootSize } = useRefSize();

	const compactLevel = useMemo(() => {
		if (rootSize.width > 730) {
			return 0;
		}

		if (rootSize.width > 600) {
			return 1;
		}

		return 2;
	}, [rootSize.width]);

	useEffect(() => {
		const orderStatus = activeOrder?.status;

		if (
			orderStatus &&
			ordersType === "executable" &&
			orderStatus !== OrderStatus.EXECUTING
		) {
			dispatch(
				orderPage.actions.setActiveOrder({
					points: [],
					distance: 0,
					route: [],
				}),
			);
		}

		if (
			orderStatus &&
			ordersType === "all" &&
			orderStatus === OrderStatus.CLOSED
		) {
			dispatch(
				orderPage.actions.setActiveOrder({
					points: [],
					distance: 0,
					route: [],
				}),
			);
		}
	}, [activeOrder, dispatch, ordersType]);

	const hasHourlyTimeField =
		!!activeOrder?.additionalFields?.rateSettings?.enableHourlyService;

	const isOwn = useMemo(() => {
		if (!isBoolean(activeOrder?.isOwn)) return true;
		return activeOrder?.isOwn;
	}, [activeOrder?.isOwn]);

	const lastReceivedOrder = useMemo(
		() => activeOrder?.additionalFields?.transfer?.lastReceivedOrder,
		[activeOrder?.additionalFields?.transfer?.lastReceivedOrder],
	);

	const hasExecutor = useMemo(() => {
		const main = activeOrder?.executorToOrder?.length;
		const transfer = lastReceivedOrder?.executorToOrder?.length;
		return !!main || !!transfer;
	}, [
		activeOrder?.executorToOrder?.length,
		lastReceivedOrder?.executorToOrder?.length,
	]);

	// An order in the archive is never edited: only "revert from the
	// archive", then edit and close again (decision 2026-10-15). The pencils
	// used to show for a closed order on the Archive tab too; the server
	// rejects such edits now. Was:
	// const isShowBtn = useMemo(() => {
	// 	if (isOwn) return true;
	// 	return hasExecutor;
	// }, [hasExecutor, isOwn]);
	const isShowBtn = useMemo(() => {
		if (activeOrder?.status === OrderStatus.CLOSED) return false;
		if (isOwn) return true;
		return hasExecutor;
	}, [activeOrder?.status, hasExecutor, isOwn]);

	const {
		created,
		feeding,
		completion,
		status,
		client,
		executor,
		classes,
		parameters,
		services,
		notes,
	} = useTopData(activeOrder, language);

	const {
		distance,

		outOfTownDistanceValue,
		outOfTownDistanceLabel,
		updateOutOfTownDistance,

		idleTimeValue,
		idleTimeLabel,
		updateIdleTime,

		priceValue,
		priceLabel,
		updatePrice,

		discountLabel,

		additionalPriceValue,
		additionalPriceLabel,
		updateAdditionalCost,

		awaitingTimeValue,
		awaitingTimeLabel,
		updateAwaitingTime,

		hourlyTimeValue,
		hourlyTimeLabel,
		updateHourlyTime,
	} = useBottomData(activeOrder);

	const receiptSection = useMemo(() => {
		if (ordersType !== ORDERS_TYPES.CLOSED) {
			return null;
		}

		if (!("id" in (activeOrder ?? {}))) {
			return null;
		}

		return (
			<OrderReceipt
				orderId={activeOrder?.id}
				receipt={activeOrder?.receipt}
				layout="inline"
				onlySuccessOrFailed
				withDivider
			/>
		);
	}, [activeOrder, ordersType]);

	return (
		<Root ref={rootRef} gaps="8px*">
			<Top compactLevel={compactLevel}>
				<Field
					area="created"
					label={t("orderPageWidgets.orderDetails.info.str0") ?? ""}
				>
					{created}
				</Field>
				<Field
					area="feeding"
					label={t("orderPageWidgets.orderDetails.info.str1") ?? ""}
				>
					{feeding}
				</Field>
				<Field
					area="completion"
					label={t("orderPageWidgets.orderDetails.info.str2") ?? ""}
				>
					{completion}
				</Field>
				<Field
					area="status"
					label={t("orderPageWidgets.orderDetails.info.str3") ?? ""}
				>
					{status}
				</Field>
				<Field
					area="paymentStatus"
					label={
						t(
							"orderPageWidgets.orderTable.modelTable.columns.paymentStatus",
						) ?? ""
					}
				>
					<OrderPaymentStatusBadge order={activeOrder} empty="-" />
				</Field>
				<Field
					area="client"
					label={t("orderPageWidgets.orderDetails.info.str4") ?? ""}
				>
					{client}
				</Field>
				<Field
					area="executor"
					label={t("orderPageWidgets.orderDetails.info.str5") ?? ""}
					font={{
						fw: "720",
						line: "14px",
					}}
				>
					{executor}
				</Field>
				<Field
					area="classes"
					label={t("orderPageWidgets.orderDetails.info.str6") ?? ""}
				>
					{classes}
				</Field>

				<Field
					area="parameters"
					label={t("orderPageWidgets.orderDetails.info.str7") ?? ""}
				>
					{parameters}
				</Field>

				<Field
					area="services"
					label={t("orderPageWidgets.orderDetails.info.str8") ?? ""}
				>
					{services}
				</Field>

				<Field
					area="notes"
					label={t("orderPageWidgets.orderDetails.info.str9") ?? ""}
				>
					{notes}
				</Field>
			</Top>
			<Divider
				orientation="horizontal"
				size="max"
				color="rgb(33, 51, 63, 0.15)"
			/>
			<Bottom
				hasHourlyTimeField={hasHourlyTimeField}
				compactLevel={compactLevel}
			>
				<Field
					area="distance"
					label={t("orderPageWidgets.orderDetails.info.str10") ?? ""}
				>
					{distance}
				</Field>
				<Field
					area="out-of-town-distance"
					label={t("orderPageWidgets.orderDetails.info.str200") ?? ""}
				>
					<span>{outOfTownDistanceLabel}</span>
					{outOfTownDistanceLabel === "-" ||
					isUndefined(outOfTownDistanceValue) ? null : (
						<AccessGuard
							accessName={
								TabAccessNames[TabKeys.OUT_OF_TOWN_DISTANCE]
							}
							canShow={isShowBtn}
						>
							<EditButton
								value={outOfTownDistanceValue}
								title={
									t(
										"orderPageWidgets.orderDetails.info.str200",
									) ?? ""
								}
								units={t(`units.km`) ?? "km"}
								onSubmit={updateOutOfTownDistance}
							/>
						</AccessGuard>
					)}
				</Field>
				<Field
					area="idle-time"
					label={t("orderPageWidgets.orderDetails.info.str11") ?? ""}
				>
					<span>{idleTimeLabel}</span>
					{idleTimeLabel === "-" ||
					isUndefined(idleTimeValue) ? null : (
						<AccessGuard
							accessName={TabAccessNames[TabKeys.IDLE_TIME]}
							canShow={isShowBtn}
						>
							<EditButton
								value={idleTimeValue}
								title={
									t(
										"orderPageWidgets.orderDetails.info.str11",
									) ?? ""
								}
								units={t(`units.minute`) ?? "min"}
								onSubmit={updateIdleTime}
							/>
						</AccessGuard>
					)}
				</Field>
				<Field
					area="price"
					label={t("orderPageWidgets.orderDetails.info.str13") ?? ""}
					styledRow={{
						alignItems: "center",
					}}
					styledSpan={{
						alignItems: "end",
					}}
				>
					{priceLabel === "-" || isUndefined(priceValue) ? (
						priceLabel
					) : (
						<PriceLabel style={{ alignItems: "start" }}>
							{priceLabel}
						</PriceLabel>
					)}
					{priceLabel === "-" || isUndefined(priceValue) ? (
						priceLabel
					) : (
						<AccessGuard
							accessName={TabAccessNames[TabKeys.PRICE]}
							canShow={isShowBtn}
						>
							<EditButton
								value={priceValue}
								title={
									t(
										"orderPageWidgets.orderDetails.info.str13",
									) ?? ""
								}
								units={currencyGlobalSettings}
								onSubmit={updatePrice}
								useMinSet
								replaceFirst
							/>
							<AdditionalPriceLabel>
								{discountLabel && `${discountLabel}`}
							</AdditionalPriceLabel>
						</AccessGuard>
					)}
				</Field>
				<Field
					area="additional-price"
					label={t("orderPageWidgets.orderDetails.info.str209") ?? ""}
				>
					<span>{additionalPriceLabel}</span>
					{additionalPriceLabel === "-" ||
					isUndefined(additionalPriceValue) ? null : (
						<AccessGuard
							accessName={
								TabAccessNames[TabKeys.ADDITIONAL_PRICE]
							}
							canShow={isShowBtn}
						>
							<EditButton
								value={additionalPriceValue}
								title={
									t(
										"orderPageWidgets.orderDetails.info.str209",
									) ?? ""
								}
								units={currencyGlobalSettings}
								onSubmit={updateAdditionalCost}
							/>
						</AccessGuard>
					)}
				</Field>
				<Field
					area="awaiting-time"
					label={t("orderPageWidgets.orderDetails.info.str15") ?? ""}
				>
					<span>{awaitingTimeLabel}</span>
					{awaitingTimeLabel === "-" ||
					isUndefined(awaitingTimeValue) ? null : (
						<AccessGuard
							accessName={TabAccessNames[TabKeys.AWAITING_TIME]}
							canShow={isShowBtn}
						>
							<EditButton
								value={awaitingTimeValue}
								title={
									t(
										"orderPageWidgets.orderDetails.info.str15",
									) ?? ""
								}
								units={t(`units.minute`) ?? "min"}
								onSubmit={updateAwaitingTime}
							/>
						</AccessGuard>
					)}
				</Field>
				{hasHourlyTimeField && (
					<Field
						area="hourly-time"
						label={
							t("orderPageWidgets.orderDetails.info.str17") ?? ""
						}
					>
						<span>{hourlyTimeLabel}</span>
						{awaitingTimeLabel === "-" ||
						isUndefined(hourlyTimeValue) ? null : (
							<AccessGuard
								accessName={TabAccessNames[TabKeys.HOURLY_TIME]}
								canShow={isShowBtn}
							>
								<EditButton
									value={hourlyTimeValue}
									title={
										t(
											"orderPageWidgets.orderDetails.info.str17",
										) ?? ""
									}
									units={t(`units.minute`) ?? "min"}
									onSubmit={updateHourlyTime}
								/>
							</AccessGuard>
						)}
					</Field>
				)}
			</Bottom>
			{receiptSection}
		</Root>
	);
};

declare namespace Info {
	interface Props {
		language: Language;
	}
}

export default Info;
